// src/pages/app-banners/app-banners.page.jsx
// Gestion des bannières publicitaires de l'app mobile TIGOON.

import React, { useEffect, useState } from 'react';
import {
  Table, Modal, Form, Input, Upload, Button, InputNumber, Switch, message,
  Breadcrumb, Tooltip, Image, Tag, Space, Popconfirm
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined,
  EyeOutlined, EyeInvisibleOutlined, RightOutlined, PictureOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { appBannerService } from '../../common/services';

export default function AppBannersPage() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await appBannerService.getAll();
      setBanners(res?.data?.banners || res?.banners || []);
    } catch (e) {
      message.error("Impossible de charger les bannières");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBanners(); }, []);

  const openCreate = () => {
    setEditing(null);
    setFileList([]);
    form.resetFields();
    form.setFieldsValue({ ordre: 0, isActive: true });
    setModalOpen(true);
  };

  const openEdit = (banner) => {
    setEditing(banner);
    setFileList([]);
    form.setFieldsValue({
      titre: banner.titre || '',
      description: banner.description || '',
      ordre: banner.ordre || 0,
      isActive: banner.isActive,
    });
    setModalOpen(true);
  };

  const onFinish = async (values) => {
    try {
      const payload = {
        titre: values.titre || '',
        description: values.description || '',
        ordre: values.ordre || 0,
        isActive: values.isActive !== false,
        image: fileList[0]?.originFileObj,
      };
      if (editing) {
        if (!payload.image) delete payload.image;
        await appBannerService.update(editing._id, payload);
        message.success("Bannière mise à jour");
      } else {
        if (!payload.image) {
          message.error("Veuillez sélectionner une image");
          return;
        }
        await appBannerService.create(payload);
        message.success("Bannière créée");
      }
      setModalOpen(false);
      fetchBanners();
    } catch (e) {
      message.error(e?.message || "Erreur lors de l'enregistrement");
    }
  };

  const onToggle = async (banner) => {
    try {
      await appBannerService.toggle(banner._id);
      message.success(banner.isActive ? "Désactivée" : "Activée");
      fetchBanners();
    } catch {
      message.error("Action impossible");
    }
  };

  const onDelete = async (banner) => {
    try {
      await appBannerService.delete(banner._id);
      message.success("Supprimée");
      fetchBanners();
    } catch {
      message.error("Suppression impossible");
    }
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      width: 120,
      render: (url) => url
        ? <Image width={100} height={56} src={url} style={{ objectFit: 'cover', borderRadius: 6 }} />
        : <PictureOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />,
    },
    {
      title: 'Titre',
      dataIndex: 'titre',
      render: (t, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{t || <span style={{ color: '#bfbfbf' }}>(sans titre)</span>}</div>
          {r.description && <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.description}</div>}
        </div>
      ),
    },
    {
      title: 'Ordre',
      dataIndex: 'ordre',
      width: 80,
      align: 'center',
      sorter: (a, b) => (a.ordre || 0) - (b.ordre || 0),
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      width: 110,
      align: 'center',
      render: (isActive, r) => (
        <Switch
          checked={isActive}
          onChange={() => onToggle(r)}
          checkedChildren="Active"
          unCheckedChildren="Off"
        />
      ),
    },
    {
      title: 'Créée',
      dataIndex: 'createdAt',
      width: 150,
      render: (d) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—',
    },
    {
      title: 'Actions',
      width: 120,
      align: 'center',
      render: (_, r) => (
        <Space>
          <Tooltip title="Modifier">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          </Tooltip>
          <Popconfirm
            title="Supprimer cette bannière ?"
            onConfirm={() => onDelete(r)}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Supprimer">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>Bannières App</Breadcrumb.Item>
        </Breadcrumb>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16,
        }}>
          <div>
            <h3 style={{ margin: 0 }}>Bannières App Mobile</h3>
            <div style={{ color: '#8c8c8c', fontSize: 13 }}>
              Affichées sur l'écran d'accueil après les services. Triées par ordre croissant.
            </div>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nouvelle bannière
          </Button>
        </div>

        <Table
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={banners}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />
      </div>

      <Modal
        title={editing ? "Modifier la bannière" : "Nouvelle bannière"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText={editing ? "Enregistrer" : "Créer"}
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item label={editing ? "Remplacer l'image (optionnel)" : "Image *"}>
            <Upload
              accept="image/*"
              listType="picture-card"
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setFileList(fileList.slice(-1))}
              onRemove={() => setFileList([])}
            >
              {fileList.length === 0 && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 6 }}>Upload</div>
                </div>
              )}
            </Upload>
            {editing && editing.imageUrl && fileList.length === 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>Image actuelle :</div>
                <Image src={editing.imageUrl} width={180} style={{ borderRadius: 6 }} />
              </div>
            )}
          </Form.Item>
          <Form.Item name="titre" label="Titre (optionnel)">
            <Input placeholder="Ex : Soldes de Noël -30%" maxLength={80} />
          </Form.Item>
          <Form.Item name="description" label="Description (optionnel)">
            <Input.TextArea rows={2} maxLength={160} placeholder="Courte phrase affichée sous le titre" />
          </Form.Item>
          <Form.Item name="ordre" label="Ordre d'affichage" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren={<EyeOutlined />} unCheckedChildren={<EyeInvisibleOutlined />} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
